import React, { useState, useEffect } from 'react';
import './RentAsicDetails.css';
import { useUser } from '../../context/UserContext';
import DepositModal from '../../components/DepositModal/DepositModal';
import { useLanguage } from '../../context/LanguageContext';
import { getTranslation } from '../../i18n/translations';

const RentAsicDetails = ({ asicId, onNavigateBack, onNavigateToOrder }) => {
  const { language } = useLanguage();
  const { user, hasSufficientBalance, getAsicSpecs, addRental } = useUser();
  const [quantity, setQuantity] = useState(1);
  const [selectedDuration, setSelectedDuration] = useState(24);
  const [selectedPaymentType, setSelectedPaymentType] = useState('monthly');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isComparisonExpanded, setIsComparisonExpanded] = useState(false);
  const [showInsufficientFunds, setShowInsufficientFunds] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [isProcessingRental, setIsProcessingRental] = useState(false);
  const [asicData, setAsicData] = useState(null);

  // Hardcoded data matching RentAsics page IDs with realistic calculations
  const mockAsicData = {
    // S21 XP Hyd - ID 1 (270 TH/s)
    1: { name: 'S21 XP Hyd 270TH/s', model: 'S21 XP Hyd', hashrate: 270, power: 3645, duration: 12, price: 1825, hostingFee: 205, dailyEarnings: 6.75, image: '/images/asic-miner.png', btcPrice: 130000 },
    '1-18': { name: 'S21 XP Hyd 270TH/s', model: 'S21 XP Hyd', hashrate: 270, power: 3645, duration: 18, price: 2737, hostingFee: 210, dailyEarnings: 7.05, image: '/images/asic-miner.png', btcPrice: 140000 },
    '1-24': { name: 'S21 XP Hyd 270TH/s', model: 'S21 XP Hyd', hashrate: 270, power: 3645, duration: 24, price: 3650, hostingFee: 215, dailyEarnings: 7.35, image: '/images/asic-miner.png', btcPrice: 155000 },
    
    // S21 Pro - ID 2 (234 TH/s)
    2: { name: 'S21 Pro 234TH/s', model: 'S21 Pro', hashrate: 234, power: 3510, duration: 12, price: 1216, hostingFee: 190, dailyEarnings: 5.85, image: '/images/asics/S21ProGrey.png', btcPrice: 130000 },
    '2-18': { name: 'S21 Pro 234TH/s', model: 'S21 Pro', hashrate: 234, power: 3510, duration: 18, price: 1825, hostingFee: 195, dailyEarnings: 6.11, image: '/images/asics/S21ProGrey.png', btcPrice: 140000 },
    '2-24': { name: 'S21 Pro 234TH/s', model: 'S21 Pro', hashrate: 234, power: 3510, duration: 24, price: 2433, hostingFee: 200, dailyEarnings: 6.37, image: '/images/asics/S21ProGrey.png', btcPrice: 155000 },
    
    // S21 - ID 3 (188 TH/s)
    3: { name: 'S21 188TH/s', model: 'S21', hashrate: 188, power: 3300, duration: 12, price: 1095, hostingFee: 185, dailyEarnings: 4.70, image: '/images/asic-miner.png', btcPrice: 130000 },
    '3-18': { name: 'S21 188TH/s', model: 'S21', hashrate: 188, power: 3300, duration: 18, price: 1642, hostingFee: 190, dailyEarnings: 4.91, image: '/images/asic-miner.png', btcPrice: 140000 },
    '3-24': { name: 'S21 188TH/s', model: 'S21', hashrate: 188, power: 3300, duration: 24, price: 2190, hostingFee: 195, dailyEarnings: 5.12, image: '/images/asic-miner.png', btcPrice: 155000 },
    
    // S19k Pro - ID 4 (120 TH/s)
    4: { name: 'S19k Pro 120TH/s', model: 'S19k Pro', hashrate: 120, power: 2760, duration: 12, price: 730, hostingFee: 175, dailyEarnings: 3.00, image: '/images/asic-miner.png', btcPrice: 130000 },
    '4-18': { name: 'S19k Pro 120TH/s', model: 'S19k Pro', hashrate: 120, power: 2760, duration: 18, price: 1095, hostingFee: 180, dailyEarnings: 3.13, image: '/images/asic-miner.png', btcPrice: 140000 },
    '4-24': { name: 'S19k Pro 120TH/s', model: 'S19k Pro', hashrate: 120, power: 2760, duration: 24, price: 1460, hostingFee: 185, dailyEarnings: 3.27, image: '/images/asic-miner.png', btcPrice: 155000 },
    
    // T21 - ID 5 (190 TH/s)
    5: { name: 'T21 190TH/s', model: 'T21', hashrate: 190, power: 3610, duration: 12, price: 973, hostingFee: 180, dailyEarnings: 4.75, image: '/images/asic-miner.png', btcPrice: 130000 },
    '5-18': { name: 'T21 190TH/s', model: 'T21', hashrate: 190, power: 3610, duration: 18, price: 1460, hostingFee: 185, dailyEarnings: 4.96, image: '/images/asic-miner.png', btcPrice: 140000 },
    '5-24': { name: 'T21 190TH/s', model: 'T21', hashrate: 190, power: 3610, duration: 24, price: 1946, hostingFee: 190, dailyEarnings: 5.17, image: '/images/asic-miner.png', btcPrice: 155000 },
    
    // S19 XP Hyd - ID 6 (255 TH/s)
    6: { name: 'S19 XP Hyd 255TH/s', model: 'S19 XP Hyd', hashrate: 255, power: 5304, duration: 12, price: 791, hostingFee: 220, dailyEarnings: 6.38, image: '/images/asic-miner.png', btcPrice: 130000 },
    '6-18': { name: 'S19 XP Hyd 255TH/s', model: 'S19 XP Hyd', hashrate: 255, power: 5304, duration: 18, price: 1186, hostingFee: 225, dailyEarnings: 6.66, image: '/images/asic-miner.png', btcPrice: 140000 },
    '6-24': { name: 'S19 XP Hyd 255TH/s', model: 'S19 XP Hyd', hashrate: 255, power: 5304, duration: 24, price: 1581, hostingFee: 230, dailyEarnings: 6.95, image: '/images/asic-miner.png', btcPrice: 155000 }
  };

  useEffect(() => {
    if (asicId) {
      // Check if URL has duration parameter
      const urlParams = new URLSearchParams(window.location.search);
      const durationParam = urlParams.get('duration');
      
      let dataKey = asicId;
      let duration = 12; // default
      
      // Map duration days to months
      if (durationParam === '365') {
        duration = 12;
        dataKey = asicId;
      } else if (durationParam === '547') {
        duration = 18;
        dataKey = `${asicId}-18`;
      } else if (durationParam === '730') {
        duration = 24;
        dataKey = `${asicId}-24`;
      }
      
      // Get data based on key, fallback to base ID if not found
      const data = mockAsicData[dataKey] || mockAsicData[asicId];
      
      if (data) {
        setAsicData(data);
        setSelectedDuration(data.duration);
      }
    }
  }, [asicId]);

  if (!asicData) {
    return <div>Loading...</div>;
  }

  const handleQuantityChange = (delta) => {
    const newQuantity = Math.max(1, quantity + delta);
    setQuantity(newQuantity);
  };

  // Calculate total mining output over contract duration
  const calculateMiningOutput = () => {
    const days = asicData.duration * 30; // approximate days
    const totalEarnings = asicData.dailyEarnings * days * quantity;
    const totalHostingCost = asicData.hostingFee * asicData.duration * quantity;
    return Math.round(totalEarnings - totalHostingCost);
  };

  // Calculate return on investment percentage
  const calculateROI = () => {
    const totalInvestment = asicData.price * quantity;
    const netProfit = calculateMiningOutput();
    return ((netProfit / totalInvestment) * 100).toFixed(2);
  };

  // Calculate breakeven point in days
  const calculateBreakeven = () => {
    const totalInvestment = asicData.price * quantity;
    const dailyNetProfit = (asicData.dailyEarnings * quantity) - (asicData.hostingFee / 30);
    if (dailyNetProfit <= 0) return 0;
    return Math.round(totalInvestment / dailyNetProfit);
  };

  const calculateTotal = () => {
    const rentalPrice = asicData.price * quantity;
    const firstMonthHosting = selectedPaymentType === 'monthly' ? asicData.hostingFee * quantity : 0;
    return rentalPrice + firstMonthHosting;
  };

  // Handle duration change
  const handleDurationChange = (newDuration) => {
    const baseId = asicId.toString().split('-')[0]; // Get base ID (e.g., "1" from "1-18")
    let newDataKey = baseId;
    let durationParam = '365';
    
    if (newDuration === 12) {
      newDataKey = baseId;
      durationParam = '365';
    } else if (newDuration === 18) {
      newDataKey = `${baseId}-18`;
      durationParam = '547';
    } else if (newDuration === 24) {
      newDataKey = `${baseId}-24`;
      durationParam = '730';
    }
    
    const newData = mockAsicData[newDataKey];
    if (newData) {
      setAsicData(newData);
      setSelectedDuration(newDuration);
      // Update URL
      window.history.pushState(
        { page: 'rent-asic-details', asicId: baseId }, 
        '', 
        `/preorder/rent-asic/${baseId}?duration=${durationParam}`
      );
    }
  };

  const handleRentClick = async () => {
    const totalCost = calculateTotal();
    
    // Check if user has sufficient balance
    if (!hasSufficientBalance(totalCost)) {
      setShowInsufficientFunds(true);
      return;
    }

    setIsProcessingRental(true);
    
    try {
      // Get ASIC specifications  
      const specs = getAsicSpecs(asicData.name);
      
      // Create rental data
      const rentalData = {
        asicModel: asicData.name,
        hashrate: specs.hashrate * quantity,
        dailyEarnings: specs.dailyEarnings * quantity,
        duration: selectedDuration,
        quantity: quantity,
        totalCost: totalCost,
        paymentType: selectedPaymentType,
        hostingFee: asicData.hostingFee
      };
      
      // Add rental to user account
      addRental(rentalData);
      
      // Show success message
      alert(`Rental successful! Your ${quantity} ${asicData.name} ASIC${quantity > 1 ? 's' : ''} will start mining within 24 hours.`);
      
      // Navigate back
      if (onNavigateBack) {
        onNavigateBack();
      }
    } catch (error) {
      console.error('Rental processing failed:', error);
      alert('Rental failed. Please try again.');
    } finally {
      setIsProcessingRental(false);
    }
  };

  const handleDepositClick = () => {
    setShowInsufficientFunds(false);
    setShowDepositModal(true);
  };

  return (
    <div className="rent-card-pre-order__container holder">
      <button className="ui-back rent-card-pre-order__back" onClick={onNavigateBack}>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M15.8333 9.99996H4.16663M4.16663 9.99996L9.99996 15.8333M4.16663 9.99996L9.99996 4.16663" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"></path>
        </svg>
        <p className="ui-back__button">{getTranslation('back', language)}</p>
      </button>

      <div className="rent-card-pre-order-container-block">
        <div className="rent-card-pre-order">
          <div className="rent-card-pre-order__block">
            <div className="rent-card-pre-order__img">
              <div className="rent-card-pre-order__img-power">
                <p className="rent-card-pre-order__img-duration">{asicData.duration} Months</p>
                <div className="rent-card-pre-order__th-power">
                  <p className="rent-card-pre-order__th-power-title">{asicData.hashrate} Th/s</p>
                  <p className="rent-card-pre-order__th-power-title border-left">{asicData.power} W</p>
                </div>
                <div className="rent-card-pre-order__image-value">
                  <img className="rent-card-pre-order__image" src={asicData.image || "/images/asic-miner.png"} alt="asic" />
                </div>
                <p className="rent-card-pre-order__image-text">
                  The Antminer {asicData.model} by Bitmain boasts a maximum hashrate of {asicData.hashrate}Th/s and consumes {asicData.power}W of power. Compact and equipped with multiple fans for optimal cooling. The device is tailored for the SHA-256 algorithm.
                </p>
                <p className="rent-card-pre-order__asic-title">ASIC Rental: {asicData.name}</p>
              </div>
            </div>

            <div className="rent-card-pre-order__info">
              <div className="rent-card-pre-order__info_group">
                <div className="rent-card-pre-order__info_button">
                  <button 
                    className="rent-card-pre-order__bottom-top"
                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                  >
                    Product Description
                    <svg 
                      width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" 
                      style={{ transform: isDescriptionExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
                    >
                      <path d="M5 7.5L10 12.5L15 7.5" stroke="#1570EF" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"></path>
                    </svg>
                  </button>
                </div>

                <div className="rent-card-pre-order__info_group-title rent-card-pre-order__info_group-title_desktop">
                  <div className="rent-card-pre-order__info_title-block">
                    <p className="rent-card-pre-order__info_title">ASIC Rental: {asicData.name}</p>
                    <p className="rent-card-pre-order__info_subtitle">Duration: {asicData.duration} Months</p>
                  </div>
                  <p className="rent-card-pre-order__info_description">
                    Rent the Bitmain Antminer {asicData.name} ({asicData.hashrate}TH), which offers a prime opportunity for efficient Bitcoin mining! It's the latest generation miner from our partner and top-manufacturer, Bitmain! The device is considered one of the most efficient and profitable Bitcoin miners on the market.
                  </p>
                </div>

                <div className="rent-card-pre-order__details_wrapper">
                  <div className="rent-card-pre-order__details rent-card-pre-order__details_border">
                    <p className="rent-card-pre-order__rental-details">Rental Details:</p>
                    <div className="rent-card-pre-order__info-value-group">
                      <div className="rent-card-pre-order__info-value">
                        <p className="rent-card-pre-order__info-title">
                          Hosting Fee per <span>{selectedPaymentType === 'monthly' ? 'Month' : 'Day'}</span>:
                        </p>
                        <p className="rent-card-pre-order__value-title">
                          ${selectedPaymentType === 'monthly' ? asicData.hostingFee.toLocaleString() : (asicData.hostingFee / 30).toFixed(2)}
                        </p>
                      </div>
                      <div className="rent-card-pre-order__info-value">
                        <p className="rent-card-pre-order__info-title">Mining Output (Total):</p>
                        <div className="rent-card-pre-order__tooltip-block">
                          <p className="rent-card-pre-order__value-title">${calculateMiningOutput().toLocaleString()}</p>
                          <div className="tooltip">
                            <div className="tooltip-text">Mining Output = Total BTC earned (in $) over {asicData.duration} months after deducting all hosting fees</div>
                            <svg className="default" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                              <path d="M9.00018 12.2727V8.99999M9.00018 5.72727H9.00836M17.182 8.99999C17.182 13.5187 13.5189 17.1818 9.00018 17.1818C4.48148 17.1818 0.818359 13.5187 0.818359 8.99999C0.818359 4.4813 4.48148 0.818176 9.00018 0.818176C13.5189 0.818176 17.182 4.4813 17.182 8.99999Z" stroke="#667085" strokeLinecap="round" strokeLinejoin="round"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="rent-card-pre-order__info-value">
                        <p className="rent-card-pre-order__info-title">Return on Investment:</p>
                        <div className="rent-card-pre-order__tooltip-block">
                          <p className="rent-card-pre-order__value-title">{calculateROI()}%</p>
                          <div className="tooltip">
                            <div className="tooltip-text">ROI = Expected profit percentage relative to the total rental cost over {asicData.duration} months</div>
                            <svg className="default" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                              <path d="M9.00018 12.2727V8.99999M9.00018 5.72727H9.00836M17.182 8.99999C17.182 13.5187 13.5189 17.1818 9.00018 17.1818C4.48148 17.1818 0.818359 13.5187 0.818359 8.99999C0.818359 4.4813 4.48148 0.818176 9.00018 0.818176C13.5189 0.818176 17.182 4.4813 17.182 8.99999Z" stroke="#667085" strokeLinecap="round" strokeLinejoin="round"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="rent-card-pre-order__info-value">
                        <p className="rent-card-pre-order__info-title">Breakeven Point:</p>
                        <div className="rent-card-pre-order__tooltip-block">
                          <p className="rent-card-pre-order__value-title">{calculateBreakeven()} days</p>
                          <div className="tooltip">
                            <div className="tooltip-text">Breakeven = Number of days required to recover your initial rental investment through mining earnings</div>
                            <svg className="default" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                              <path d="M9.00018 12.2727V8.99999M9.00018 5.72727H9.00836M17.182 8.99999C17.182 13.5187 13.5189 17.1818 9.00018 17.1818C4.48148 17.1818 0.818359 13.5187 0.818359 8.99999C0.818359 4.4813 4.48148 0.818176 9.00018 0.818176C13.5189 0.818176 17.182 4.4813 17.182 8.99999Z" stroke="#667085" strokeLinecap="round" strokeLinejoin="round"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="rent-card-pre-order__info-value">
                        <p className="rent-card-pre-order__info-title">Daily Earnings (Avg):</p>
                        <div className="rent-card-pre-order__tooltip-block">
                          <p className="rent-card-pre-order__value-title">${(asicData.dailyEarnings * quantity).toFixed(2)}</p>
                          <div className="tooltip">
                            <div className="tooltip-text">Average daily mining earnings based on current difficulty and ${asicData.btcPrice.toLocaleString()} BTC price</div>
                            <svg className="default" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                              <path d="M9.00018 12.2727V8.99999M9.00018 5.72727H9.00836M17.182 8.99999C17.182 13.5187 13.5189 17.1818 9.00018 17.1818C4.48148 17.1818 0.818359 13.5187 0.818359 8.99999C0.818359 4.4813 4.48148 0.818176 9.00018 0.818176C13.5189 0.818176 17.182 4.4813 17.182 8.99999Z" stroke="#667085" strokeLinecap="round" strokeLinejoin="round"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rent-card-pre-order__details rent-card-pre-order__details_style">
                    <div className="rent-card-pre-order__details-block">
                      <div className="rent-card-pre-order__details-title-block">
                        <p className="rent-card-pre-order__details-title">Rental Price per 1 ASIC:</p>
                        <p className="rent-card-pre-order__details-value">$ {asicData.price.toLocaleString()}</p>
                      </div>
                      <div className="rent-card-pre-order__details-info">
                        <div className="rent-card-pre-order__details-duration">
                          <p className="rent-card-pre-order__duration-title">Duration:</p>
                          <div className="rent-card-pre-order__duration-value-block">
                            <button 
                              className={`rent-card-pre-order__duration-value ${asicData.duration === 12 ? 'rent-card-pre-order__hosting-active' : ''}`}
                              onClick={() => handleDurationChange(12)}
                            >
                              12
                            </button>
                            <button 
                              className={`rent-card-pre-order__duration-value ${asicData.duration === 18 ? 'rent-card-pre-order__hosting-active' : ''}`}
                              onClick={() => handleDurationChange(18)}
                            >
                              18
                            </button>
                            <button 
                              className={`rent-card-pre-order__duration-value ${asicData.duration === 24 ? 'rent-card-pre-order__hosting-active' : ''}`}
                              onClick={() => handleDurationChange(24)}
                            >
                              24
                            </button>
                          </div>
                        </div>
                        <div className="rent-card-pre-order__details-hosting">
                          <p className="rent-card-pre-order__hosting-title">Hosting Fee Payment:</p>
                          <div className="rent-card-pre-order__hosting-block">
                            <button 
                              className={`rent-card-pre-order__hosting-monthly ${selectedPaymentType === 'monthly' ? 'rent-card-pre-order__hosting-active' : ''}`}
                              onClick={() => setSelectedPaymentType('monthly')}
                            >
                              Monthly
                              <div className="tooltip rent-card-pre-order__hosting-monthly-tooltip">
                                <div className="tooltip-text">The hosting fee for the first month is included in the rental price.</div>
                                <svg className="absolut" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                  <g clipPath="url(#clip0_264_44934)">
                                    <path d="M9.00018 12.2727V8.99999M9.00018 5.72727H9.00836M17.182 8.99999C17.182 13.5187 13.5189 17.1818 9.00018 17.1818C4.48148 17.1818 0.818359 13.5187 0.818359 8.99999C0.818359 4.4813 4.48148 0.818176 9.00018 0.818176C13.5189 0.818176 17.182 4.4813 17.182 8.99999Z" stroke="#667085" strokeLinecap="round" strokeLinejoin="round"></path>
                                  </g>
                                  <defs>
                                    <clipPath id="clip0_264_44934">
                                      <rect width="18" height="18" fill="white"></rect>
                                    </clipPath>
                                  </defs>
                                </svg>
                              </div>
                            </button>
                            <button 
                              className={`rent-card-pre-order__hosting-monthly ${selectedPaymentType === 'daily' ? 'rent-card-pre-order__hosting-active' : ''}`}
                              onClick={() => setSelectedPaymentType('daily')}
                            >
                              Daily
                              <div className="tooltip rent-card-pre-order__hosting-monthly-tooltip">
                                <div className="tooltip-text">The hosting fee for the first month is included in the rental price.</div>
                                <svg className="absolut" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                  <g clipPath="url(#clip0_264_44934)">
                                    <path d="M9.00018 12.2727V8.99999M9.00018 5.72727H9.00836M17.182 8.99999C17.182 13.5187 13.5189 17.1818 9.00018 17.1818C4.48148 17.1818 0.818359 13.5187 0.818359 8.99999C0.818359 4.4813 4.48148 0.818176 9.00018 0.818176C13.5189 0.818176 17.182 4.4813 17.182 8.99999Z" stroke="#667085" strokeLinecap="round" strokeLinejoin="round"></path>
                                  </g>
                                  <defs>
                                    <clipPath id="clip0_264_44934">
                                      <rect width="18" height="18" fill="white"></rect>
                                    </clipPath>
                                  </defs>
                                </svg>
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card-input_button-block">
                      <div className="card-input_button">
                        <p className="card-input_button__title">Quantity:</p>
                        <div className="card-input_button__block">
                          <button className="transparent card-input_button__button" onClick={() => handleQuantityChange(-1)}>-</button>
                          <p className="card-input_button__button">{quantity}</p>
                          <button className="transparent card-input_button__button" onClick={() => handleQuantityChange(1)}>+</button>
                        </div>
                      </div>
                      <div className="rent-card-pre-order__total-block rent-card-pre-order__total_mobile">
                        <p className="rent-card-pre-order__total">Total Price:</p>
                        <p className="rent-card-pre-order__total-value">$ {calculateTotal().toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="rent-card-pre-order__button-block">
                      <div className="rent-card-pre-order__total-block rent-card-pre-order__total_desktop">
                        <p className="rent-card-pre-order__total">Total Price:</p>
                        <p className="rent-card-pre-order__total-value">$ {calculateTotal().toLocaleString()}</p>
                      </div>
                      <button className="orange rent-card-pre-order__btn-link" onClick={handleRentClick}>
                        Rent
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Section */}
      <div className="asic-comparison-section holder">
        <div className="asic-comparison-container">
          <div className="asic-comparison-header" onClick={() => setIsComparisonExpanded(!isComparisonExpanded)}>
            <h2 className="asic-comparison-title">
              <span className="comparison-icon">⚖️</span>
              Renting vs. Buying ASIC Comparison
            </h2>
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 20 20" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ transform: isComparisonExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
            >
              <path d="M5 7.5L10 12.5L15 7.5" stroke="#1570EF" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"></path>
            </svg>
          </div>
          
          {isComparisonExpanded && (
            <div className="asic-comparison-content">
              <div className="comparison-grid">
                <div className="comparison-card comparison-card--rent">
                  <div className="comparison-card-header">
                    <h3 className="comparison-card-title">💎 Rent ASIC</h3>
                    <span className="comparison-badge comparison-badge--recommended">Recommended</span>
                  </div>
                  <div className="comparison-card-body">
                    <div className="comparison-metric">
                      <span className="metric-label">Initial Cost:</span>
                      <span className="metric-value metric-value--green">${asicData.price.toLocaleString()}</span>
                    </div>
                    <div className="comparison-metric">
                      <span className="metric-label">Monthly Hosting:</span>
                      <span className="metric-value">${asicData.hostingFee.toLocaleString()}/mo</span>
                    </div>
                    <div className="comparison-metric">
                      <span className="metric-label">Contract Duration:</span>
                      <span className="metric-value">{asicData.duration} months</span>
                    </div>
                    <div className="comparison-metric">
                      <span className="metric-label">Total Investment:</span>
                      <span className="metric-value">${(asicData.price + (asicData.hostingFee * asicData.duration)).toLocaleString()}</span>
                    </div>
                    <div className="comparison-metric">
                      <span className="metric-label">Expected ROI:</span>
                      <span className="metric-value metric-value--highlight">{calculateROI()}%</span>
                    </div>
                    <div className="comparison-pros">
                      <h4 className="pros-title">✓ Benefits:</h4>
                      <ul className="pros-list">
                        <li>Low upfront cost</li>
                        <li>No maintenance hassles</li>
                        <li>Professional hosting included</li>
                        <li>Flexible contract terms</li>
                        <li>Start mining immediately</li>
                        <li>Expert technical support</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="comparison-card comparison-card--buy">
                  <div className="comparison-card-header">
                    <h3 className="comparison-card-title">🏭 Buy ASIC</h3>
                  </div>
                  <div className="comparison-card-body">
                    <div className="comparison-metric">
                      <span className="metric-label">Initial Cost:</span>
                      <span className="metric-value metric-value--red">${(asicData.price * 2.5).toLocaleString()}</span>
                    </div>
                    <div className="comparison-metric">
                      <span className="metric-label">Setup Cost:</span>
                      <span className="metric-value">$500-$1,500</span>
                    </div>
                    <div className="comparison-metric">
                      <span className="metric-label">Monthly Power:</span>
                      <span className="metric-value">$250-$400/mo</span>
                    </div>
                    <div className="comparison-metric">
                      <span className="metric-label">Total Investment:</span>
                      <span className="metric-value">${((asicData.price * 2.5) + 1000 + (300 * asicData.duration)).toLocaleString()}</span>
                    </div>
                    <div className="comparison-metric">
                      <span className="metric-label">Equipment Value:</span>
                      <span className="metric-value">Depreciates ~50%/yr</span>
                    </div>
                    <div className="comparison-cons">
                      <h4 className="cons-title">⚠ Challenges:</h4>
                      <ul className="cons-list">
                        <li>High upfront investment</li>
                        <li>Maintenance responsibility</li>
                        <li>Electricity costs vary</li>
                        <li>Hardware depreciation</li>
                        <li>Space & cooling required</li>
                        <li>Technical expertise needed</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="comparison-summary">
                <div className="summary-card">
                  <h4 className="summary-title">💰 Cost Savings</h4>
                  <p className="summary-value">Save ${((asicData.price * 2.5) + 1000 + (300 * asicData.duration) - (asicData.price + (asicData.hostingFee * asicData.duration))).toLocaleString()}</p>
                  <p className="summary-description">by renting instead of buying over {asicData.duration} months</p>
                </div>
                <div className="summary-card">
                  <h4 className="summary-title">⏱️ Time to Profit</h4>
                  <p className="summary-value">{calculateBreakeven()} days</p>
                  <p className="summary-description">to recover your initial rental investment</p>
                </div>
                <div className="summary-card">
                  <h4 className="summary-title">📈 Flexibility</h4>
                  <p className="summary-value">Zero Hassle</p>
                  <p className="summary-description">Switch or upgrade anytime without selling hardware</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer Section */}
      <div className="disclaimer-section holder">
        <div className="disclaimer-card">
          <div className="disclaimer-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3 className="disclaimer-title">Important Disclaimer</h3>
          </div>
          <p className="disclaimer-description">
            These estimates offer forward-looking information based on current data, including mining difficulty: FPPS 0.00000041, and BTC reference rate: ${asicData.btcPrice.toLocaleString()} ({asicData.duration}m projection). Calculations assume {quantity}x {asicData.name} unit(s) with {asicData.hashrate} TH/s hashrate. These are not guarantees of future results and should be viewed with caution due to market uncertainties. Actual mining output may vary based on network difficulty, BTC price fluctuations, and equipment performance.
          </p>
        </div>
      </div>

      {/* Insufficient Funds Modal */}
      {showInsufficientFunds && (
        <div className="insufficient-funds-modal-overlay">
          <div className="insufficient-funds-modal">
            <div className="insufficient-funds-modal__header">
              <h3 className="insufficient-funds-modal__title">Insufficient Balance</h3>
              <button 
                className="insufficient-funds-modal__close"
                onClick={() => setShowInsufficientFunds(false)}
              >
                ×
              </button>
            </div>
            <div className="insufficient-funds-modal__content">
              <div className="insufficient-funds-modal__info">
                <div className="insufficient-funds-modal__icon">💰</div>
                <p className="insufficient-funds-modal__message">
                  You need ${calculateTotal().toLocaleString()} to rent this ASIC, but your current balance is ${user.balance.toFixed(2)}.
                </p>
                <p className="insufficient-funds-modal__required">
                  Additional ${(calculateTotal() - user.balance).toFixed(2)} required.
                </p>
              </div>
              <div className="insufficient-funds-modal__actions">
                <button 
                  className="insufficient-funds-modal__cancel"
                  onClick={() => setShowInsufficientFunds(false)}
                >
                  Cancel
                </button>
                <button 
                  className="insufficient-funds-modal__deposit"
                  onClick={handleDepositClick}
                >
                  Deposit Funds
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDepositModal && (
        <DepositModal
          isOpen={showDepositModal}
          onClose={() => setShowDepositModal(false)}
          currency="BTC"
          minAmount={calculateTotal() - user.balance}
        />
      )}
    </div>
  );
};

export default RentAsicDetails;